// src/components/Email.jsx
import React, { useContext, useEffect, useRef, useState } from "react";
import { Grid, useTheme } from "@mui/material";
import { useLocation } from "react-router-dom";
import { useFormContext } from "../Context/Context";
import { useInputValues } from "../Context/InputValuesContext";
import { useErrorContext } from "../Context/ErrorContext";
import { useReadOnly } from "../Context/ReadOnlyContext";
import { FinalValuesContext } from "../Context/FinalValuesContext";
import ConvertTitle from "./ConvertTitle";
import EditableInput from "./Email/EditableInput";
import {
  getInitialValues,
  updateDrawerState,
  validateEmail,
} from "./Email/emailHelpers";

const Email = ({ id, header }) => {
  /* ---------- Context & Router ---------- */
  const { drawerData } = useFormContext();
  const { inputValues } = useInputValues();
  const { setMistake } = useErrorContext();
  const { readOnly } = useReadOnly();
  const { finalSelectValues } = useContext(FinalValuesContext);
  const location = useLocation();
  const currentPage = location.pathname;
  const theme = useTheme();
  const HeadingColor = theme.palette.blue[700];

  /* ---------- Indices ---------- */
  const idx = drawerData.findIndex((c) => c.id === id);
  const fieldCondIdx = finalSelectValues.findIndex(
    (i) => i.name === "FieldCondition"
  );
  const fieldCondTitle = ConvertTitle({
    title: finalSelectValues[fieldCondIdx]?.value,
  });

  /* ---------- Local State ---------- */
  const init = getInitialValues(drawerData, idx);
  const [emailValue, setEmailValue] = useState(
    header || init.emailValue
  );
  const [mainEmailInput, setMainEmailInput] = useState(init.mainEmailInput);
  const [exEmailValue, setExEmailValue] = useState(init.exEmailValue);
  const [emailError, setEmailError] = useState("");
  const [border, setBorder] = useState(false);
  const [condition, setCondition] = useState(false);
  const [isEmailFocused, setIsEmailFocused] = useState(false);


  /* ---------- Helpers ---------- */
  const isReadOnlyGlobal =
    currentPage.startsWith("/publish") ||
    currentPage.startsWith("/user/fill-form/");

  /* ---------- Condition Logic ---------- */
  useEffect(() => {
    const dataIdx = drawerData.findIndex((d) => d.title === fieldCondTitle);
    const dataCond = drawerData[dataIdx]?.state?.readOnly;
    const silentReadOnly = readOnly || dataCond;
    setCondition(
      currentPage === "/publish" && silentReadOnly && fieldCondTitle === "Email"
    );
  }, [finalSelectValues, readOnly]);

  /* ---------- Drawer Sync ---------- */
  useEffect(() => {
    updateDrawerState(
      drawerData,
      idx,
      {
        emailValue,
        mainEmailInput,
        exEmailValue,
      },
      readOnly
    );
  }, [emailValue, mainEmailInput, exEmailValue]);

  /* ---------- Validation ---------- */
  const errMsg =
    inputValues?.find((i) => i.name === "EnterAValidEmailAddress")?.value ||
    "آدرس ایمیل معتبر نیست";

  const handleMainEmailChange = (e) => {
    const val = e.target.value;
    setMainEmailInput(val);
    if (!validateEmail(val)) {
      setEmailError(errMsg);
      setMistake(true);
    } else {
      setEmailError("");
      setMistake(false);
    }
  };

  /* ---------- UI ---------- */
  const gridRef = useRef(null);
  useEffect(() => {
    const handler = (e) =>
      gridRef.current && !gridRef.current.contains(e.target) && setBorder(false);
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  return (
    <Grid
      ref={gridRef}
      bgcolor="white"
      p="12px"
      mb="28px"
      borderRadius="3px"
      border={
        border && drawerData[idx] && !isReadOnlyGlobal
          ? "2px solid #0293e2"
          : "2px solid transparent"
      }
      onClick={() => setBorder(true)}
    >
      <EditableInput
        value={emailValue}
        onChange={(e) => setEmailValue(e.target.value)}
        readOnly={isReadOnlyGlobal || condition}
        fontSize="16px"
        sx={{ border: "none", color: HeadingColor }}
        placeholder="یک عنوان وارد کنید"
      />

      <EditableInput
        type="email"
        value={mainEmailInput}
        onChange={handleMainEmailChange}
        readOnly={condition}
        error={emailError}
        fontSize="12px"
        placeholder="you@example.com"
        sx={{ mt: "8px" }}
        onFocus={() => setIsEmailFocused(true)}
        onBlur={() => setIsEmailFocused(false)}
      />

      <EditableInput
        value={exEmailValue}
        onChange={(e) => setExEmailValue(e.target.value)}
        readOnly={isReadOnlyGlobal || condition}
        fontSize="12px"
        sx={{ border: "none", mt: "8px", color: "#b8bdc9" }}
        placeholder="example@example.com"
      />
    </Grid>
  );
};

export default Email;
